import { Typography } from 'antd'
import { IAddVariation, ICharacteristic, IFrontVariation } from 'models/product'
import { showMessage } from 'shared/utils/showMessage'

interface IErrorProductProps {
  variation: IFrontVariation
}

const ErrorProduct: React.FC<IErrorProductProps> = ({ variation }) => {
  return (
    <>
      <Typography.Text style={{ fontSize: 16, fontWeight: 600, display: 'block', marginBottom: 7 }}>Выберите товар для вариации</Typography.Text>
      {variation.firstCharacteristicValue && (
        <Typography.Text style={{ fontSize: 16, fontWeight: 500, display: 'block' }}>{variation.firstCharacteristicName}: {variation.firstCharacteristicValue}</Typography.Text>
      )}
      {variation.secondCharacteristicValue && (
        <Typography.Text style={{ fontSize: 16, fontWeight: 500, display: 'block' }}>{variation.secondCharacteristicName}: {variation.secondCharacteristicValue}</Typography.Text>
      )}
    </>
  )
}

// Пустая строка-заготовка (без значений и без товара) не участвует в валидации и сабмите.
// Отбрасывать «последнюю» строку по индексу нельзя: заполненная строка без хвостовой пустой
// (значение вставлено из буфера / товар выбран через модалку) молча терялась и вариация не привязывалась.
const isEmptyPlaceholder = (item: IFrontVariation): boolean =>
  !item.firstCharacteristicValue?.trim() && !item.secondCharacteristicValue?.trim() && !item.product

// ключ комбинации значений строки: «размер|цвет» без регистра и пробелов по краям
const getCombinationKey = (item: IFrontVariation): string =>
  `${(item.firstCharacteristicValue ?? '').trim().toLowerCase()}|${(item.secondCharacteristicValue ?? '').trim().toLowerCase()}`

export const validateVariations = (
  variations: IFrontVariation[],
  // характеристики основного товара — его значения считаются одной из комбинаций
  parentCharacteristics: ICharacteristic[] = []
): { isError: boolean } => {
  const clearedVariations = variations.filter((item) => !isEmptyPlaceholder(item))
  for (let i = 0; i < clearedVariations.length; i++) {
    const item = clearedVariations[i]
    if (!item.firstCharacteristicValue && !item.secondCharacteristicValue && item.product) {
      showMessage('error', `Заполните одно из значений для ${i + 1} вариации, либо удалите товар`, '')
      return { isError: true }
    }
    if ((item.firstCharacteristicValue || item.secondCharacteristicValue) && !item.product) {
      showMessage('error', <ErrorProduct variation={item} />, '')
      return { isError: true }
    }
  }

  // Комбинации значений не должны повторяться между вариациями
  const seenCombinations = new Set<string>()
  for (let i = 0; i < clearedVariations.length; i++) {
    const key = getCombinationKey(clearedVariations[i])
    if (seenCombinations.has(key)) {
      showMessage('error', `Вариация ${i + 1} дублирует значения характеристик другой вариации`, '')
      return { isError: true }
    }
    seenCombinations.add(key)
  }

  // ...и не должны совпадать со значениями основного товара — он сам является одной из комбинаций
  const parentValueByName = new Map(
    parentCharacteristics
      .filter((c) => c?.name?.trim() && c?.value?.trim())
      .map((c) => [c.name.trim().toLowerCase(), c.value.trim().toLowerCase()])
  )
  for (let i = 0; i < clearedVariations.length; i++) {
    const item = clearedVariations[i]
    const matchesParent = (name?: string, value?: string) =>
      !!name?.trim() &&
      parentValueByName.get(name.trim().toLowerCase()) === (value ?? '').trim().toLowerCase()

    const firstMatches =
      !item.firstCharacteristicValue?.trim() ||
      matchesParent(item.firstCharacteristicName, item.firstCharacteristicValue)
    const secondMatches =
      !item.secondCharacteristicValue?.trim() ||
      matchesParent(item.secondCharacteristicName, item.secondCharacteristicValue)
    const hasAnyValue =
      !!item.firstCharacteristicValue?.trim() || !!item.secondCharacteristicValue?.trim()

    if (hasAnyValue && firstMatches && secondMatches) {
      showMessage('error', `Вариация ${i + 1} повторяет значения характеристик основного товара`, '')
      return { isError: true }
    }
  }

  return { isError: false }
}

export const getNormalizedVariations = (variations: IFrontVariation[] | undefined, firstVariationName: string | undefined, secondVariationName: string | undefined): IAddVariation[] | undefined => {
  if (!variations) {
    return undefined
  }
  const clearedVariations = variations.filter((item) => !isEmptyPlaceholder(item))
  return clearedVariations.filter(item => (item.firstCharacteristicValue || item.secondCharacteristicValue) && item.product).map(item => {
    if (item.firstCharacteristicValue && item.secondCharacteristicValue) {
      return {
        _id: item.product?.id,
        characteristic: [
          {
            name: firstVariationName,
            value: item.firstCharacteristicValue
          },
          {
            name: secondVariationName,
            value: item.secondCharacteristicValue
          }
        ]
      } as IAddVariation
    }
    if (item.firstCharacteristicValue) {
      return {
        _id: item.product?.id,
        characteristic: [
          {
            name: firstVariationName,
            value: item.firstCharacteristicValue
          }
        ]
      } as IAddVariation
    }
    return {
      _id: item.product?.id,
      characteristic: [
        {
          name: firstVariationName,
          value: ''
        },
        {
          name: secondVariationName,
          value: item.secondCharacteristicValue
        }
      ]
    } as IAddVariation
  }) as IAddVariation[]
}
